// src/components/seating-chart/SeatInventoryPanel.js  (NEW FILE)
//
// The "inventory" shown next to the map: every ticket of the current order with its seat, category and price,
// a delete button per ticket, the total, and a hint + "Suggest seats" button while some tickets have no seat.
//
// rows: [{ key, kind: "seated" | "unassigned", ticketTypeId, seatId?, categoryId? }]   (from useSeatSelection)

import React from "react";
import { Alert, Box, Button, Card, Divider, IconButton, Stack, Typography } from "@mui/material";

import Iconify from "src/components/iconify";
import { fCurrency } from "src/utils/formatNumber";
import { TrashIcon } from "src/assets/icons/TrashIcon";
import { useLocales } from "src/locales";

const formatClock = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

const SeatInventoryPanel = ({
  rows,
  ticketTypesById,
  categoryById,
  seatLabelById,
  currency = "",
  totalPrice,
  unassignedCount,
  secondsLeft,
  onRemove,
  onSuggest,
  listMaxHeight = "none",
}) => {
  const { translate } = useLocales();

  const money = (value) => (value ? `${fCurrency(value)} ${currency}` : translate("seatingChart.free"));

  return (
    <Card variant="outlined" sx={{ p: 2, display: "flex", flexDirection: "column", minHeight: 0 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5, flexShrink: 0 }}>
        <Typography variant="subtitle1">
          {translate("seatingChart.yourTickets")}{rows.length ? ` (${rows.length})` : ""}
        </Typography>
        {secondsLeft !== null && (
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: secondsLeft < 60 ? "error.main" : "text.secondary" }}>
            <Iconify icon="eva:clock-outline" width={16} />
            <Typography variant="caption">{formatClock(secondsLeft)}</Typography>
          </Stack>
        )}
      </Stack>

      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          {translate("seatingChart.noTicketsYet")}
        </Typography>
      ) : (
        <Stack spacing={1} sx={{ maxHeight: listMaxHeight, flex: "1 1 auto", minHeight: 96, overflowY: "auto", pr: 0.5 }}>
          {rows.map((row) => {
            const type = ticketTypesById[row.ticketTypeId];
            const category = row.categoryId ? categoryById.get(row.categoryId) : null;
            const seated = row.kind === "seated";
            return (
              <Card key={row.key} variant="outlined" sx={{ p: 1.25, flexShrink: 0, borderStyle: seated ? "solid" : "dashed" }}>
                <Stack direction="row" alignItems="center" spacing={1.25}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      flexShrink: 0,
                      bgcolor: category?.color ?? "transparent",
                      border: category ? "none" : "2px dashed",
                      borderColor: "text.disabled",
                    }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" noWrap>
                      {type?.name}
                    </Typography>
                    {seated ? (
                      <Typography variant="caption" color="text.secondary" display="block" noWrap>
                        {[category?.name, seatLabelById.get(row.seatId)].filter(Boolean).join(" · ")}
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="warning.main" display="block" sx={{ fontStyle: "italic" }}>
                        {translate("seatingChart.noSeatSelected")}
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
                    {money(type?.price)}
                  </Typography>
                  <IconButton size="small" aria-label="Remove ticket" onClick={() => onRemove(row)}>
                    <TrashIcon />
                  </IconButton>
                </Stack>
              </Card>
            );
          })}
        </Stack>
      )}

      {unassignedCount > 0 && (
        <Alert
          severity="warning"
          sx={{ mt: 2, alignItems: "center", flexShrink: 0 }}
          action={
            <Button color="inherit" size="small" onClick={() => onSuggest()}>
              {translate("seatingChart.suggestSeats")}
            </Button>
          }
        >
          {unassignedCount === 1 ? translate("seatingChart.oneTicketNeedsSeat") : `${unassignedCount} ${translate("seatingChart.manyTicketsNeedSeat")}`}
        </Alert>
      )}

      <Divider sx={{ my: 2, flexShrink: 0 }} />
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ flexShrink: 0 }}>
        <Typography variant="subtitle1">Total</Typography>
        <Typography variant="subtitle1">{money(totalPrice)}</Typography>
      </Stack>
    </Card>
  );
};

export default SeatInventoryPanel;